import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Badge, Button, Spin, Tooltip, notification, Input } from 'antd';
import {
  MessageOutlined, SendOutlined, ArrowLeftOutlined, TeamOutlined,
  PlusOutlined, UserOutlined,
} from '@ant-design/icons';
import { io } from 'socket.io-client';
import { useLocation } from 'react-router-dom';
import { socketUrl } from '../../config';
import {
  getConversations, getMessages, sendMessage, markRead,
  getContacts, getOrCreateDirect,
} from '../../pages/sn_staff_chat/service';

// ── Helpers ───────────────────────────────────────────────────────────────────
function getCurrentUserId() {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).id;
  } catch (_) { return null; }
}

function initials(name) {
  if (!name) return '?';
  return name.trim().split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function FloatingChat() {
  const location = useLocation();
  const myId = getCurrentUserId();

  // view: 'convs' | 'contacts' | 'messages'
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('convs');

  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');

  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState(new Set());

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isOnChatPage = location.pathname === '/staff-chat';

  // ── Load conversations ───────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      const data = await getConversations();
      const convs = data.conversations || [];
      setConversations(convs);
      setTotalUnread(convs.reduce((s, c) => s + (c.unread_count || 0), 0));
    } catch (_) {}
  }, []);

  // ── Load contacts ────────────────────────────────────────────────────────
  const loadContacts = useCallback(async () => {
    try {
      const data = await getContacts();
      setContacts(data.contacts || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadConversations(); loadContacts(); }, [loadConversations, loadContacts]);

  // ── Socket ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    // ── Online presence ──
    socket.emit('get_online_users');
    socket.on('online_users', ({ userIds }) => {
      setOnlineUsers(new Set(userIds));
    });
    socket.on('user_online', ({ userId: uid }) => {
      setOnlineUsers(prev => new Set([...prev, uid]));
    });
    socket.on('user_offline', ({ userId: uid }) => {
      setOnlineUsers(prev => { const s = new Set(prev); s.delete(uid); return s; });
    });

    socket.on('new_message', (msg) => {
      if (msg.sender_id === myId) return;

      setActiveConv(prev => {
        if (prev && prev.id === msg.conversation_id) {
          setMessages(old => [...old, msg]);
          markRead(msg.conversation_id).catch(() => {});
        }
        return prev;
      });

      loadConversations();

      // Toast if message isn't visible
      setOpen(isOpen => {
        setView(v => {
          setActiveConv(active => {
            const visible = isOpen && v === 'messages' && active && active.id === msg.conversation_id;
            if (!visible) {
              notification.open({
                message: msg.sender_name || 'New Message',
                description: msg.message.length > 70 ? msg.message.slice(0, 70) + '…' : msg.message,
                icon: <MessageOutlined style={{ color: '#7a0000' }} />,
                duration: 4,
                placement: 'bottomRight',
                style: { cursor: 'pointer', marginBottom: 70 },
                onClick: () => setOpen(true),
              });
            }
            return active;
          });
          return v;
        });
        return isOpen;
      });
    });

    return () => { socket.disconnect(); socketRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Focus input when messages view opens ─────────────────────────────────
  useEffect(() => {
    if (view === 'messages') setTimeout(() => inputRef.current?.focus(), 100);
  }, [view]);

  // ── Open conversation ────────────────────────────────────────────────────
  const openConv = async (conv) => {
    setActiveConv(conv);
    setMessages([]);
    setView('messages');
    setLoadingMsgs(true);
    if (socketRef.current) socketRef.current.emit('join_conversation', conv.id);
    try {
      const data = await getMessages(conv.id);
      setMessages(data.messages || []);
      await markRead(conv.id);
      setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c));
      setTotalUnread(prev => Math.max(0, prev - (conv.unread_count || 0)));
    } catch (_) {}
    finally { setLoadingMsgs(false); }
  };

  // ── Start direct chat from contacts view ─────────────────────────────────
  const startDirect = async (contactId) => {
    try {
      const data = await getOrCreateDirect(contactId);
      await loadConversations();
      // Re-fetch updated list to find the conv object
      const res = await getConversations();
      const convs = res.conversations || [];
      setConversations(convs);
      const conv = convs.find(c => c.id === data.conversation_id);
      if (conv) { openConv(conv); }
      else {
        // Build a minimal conv object if not in list yet
        const contact = contacts.find(c => c.id === contactId);
        openConv({ id: data.conversation_id, type: 'direct', other_name: contact?.name || '', unread_count: 0 });
      }
      setContactSearch('');
    } catch (_) {}
  };

  // ── Send message ─────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!inputText.trim() || !activeConv || sending) return;
    const text = inputText.trim();
    setInputText('');
    setSending(true);
    try {
      const data = await sendMessage(activeConv.id, text);
      setMessages(prev => [...prev, data.message]);
      loadConversations();
    } catch (_) { setInputText(text); }
    finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  function convName(conv) {
    return conv.type === 'group' ? (conv.group_name || 'Group') : (conv.other_name || 'Unknown');
  }

  const filteredContacts = contacts.filter(c =>
    !contactSearch || c.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(contactSearch.toLowerCase())
  );

  if (isOnChatPage) return null;

  // ── Drawer header ─────────────────────────────────────────────────────────
  const drawerTitle = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {(view === 'messages' || view === 'contacts') && (
        <Button
          type="text" size="small" icon={<ArrowLeftOutlined />}
          onClick={() => { setView('convs'); setActiveConv(null); setContactSearch(''); }}
          style={{ padding: '0 6px', marginRight: 2, color: '#fff' }}
        />
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', lineHeight: 1.2 }}>
          {view === 'messages' ? convName(activeConv || {})
            : view === 'contacts' ? 'New Chat'
            : 'Chat'}
        </div>
        {view === 'messages' && activeConv && activeConv.type === 'direct' && activeConv.other_id && (
          <div style={{ fontSize: 11, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%', display: 'inline-block',
              background: onlineUsers.has(activeConv.other_id) ? '#52c41a' : '#bfbfbf',
            }} />
            <span style={{ color: onlineUsers.has(activeConv.other_id) ? '#b7eb8f' : 'rgba(255,255,255,0.6)' }}>
              {onlineUsers.has(activeConv.other_id) ? 'Online' : 'Offline'}
            </span>
          </div>
        )}
      </div>
      {view === 'convs' && (
        <Tooltip title="Start new chat">
          <Button
            type="text" size="small" icon={<PlusOutlined />}
            onClick={() => setView('contacts')}
            style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16 }}
          />
        </Tooltip>
      )}
    </div>
  );

  return (
    <>
      {/* ── FAB ── */}
      <style>{`
        @keyframes fcPulse {
          0%   { box-shadow: 0 0 0 0 rgba(26,46,74,0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(26,46,74,0); }
          100% { box-shadow: 0 0 0 0 rgba(26,46,74,0); }
        }
      `}</style>
      <div style={{ position: 'fixed', bottom: 72, left: 18, zIndex: 1200 }}>
        <Tooltip title="Chat" placement="right">
          <Badge count={totalUnread} overflowCount={99} size="small">
            <Button
              type="primary"
              onClick={() => { setOpen(prev => { if (!prev) loadConversations(); return !prev; }); }}
              icon={<MessageOutlined style={{ fontSize: 20 }} />}
              style={{
                width: 54, height: 54, borderRadius: '50%',
                background: '#7a0000',
                border: 'none',
                boxShadow: '0 4px 20px rgba(26,46,74,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: totalUnread > 0 ? 'fcPulse 1.8s ease-out infinite' : 'none',
              }}
            />
          </Badge>
        </Tooltip>
      </div>

      {/* ── Backdrop — click outside to close ── */}
      {open && (
        <div
          onClick={() => { setOpen(false); setView('convs'); setActiveConv(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 1099, background: 'rgba(0,0,0,0.18)' }}
        />
      )}

      {/* ── Drawer ── */}
      {open && (
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 360,
          zIndex: 1100, background: '#fff',
          boxShadow: '-6px 0 32px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column',
          animation: 'fcSlideIn 0.22s cubic-bezier(0.25,0.8,0.25,1)',
        }}>
        <style>{`
          @keyframes fcSlideIn {
            from { transform: translateX(100%); opacity: 0; }
            to   { transform: translateX(0);    opacity: 1; }
          }
        `}</style>
          {/* Header */}
          <div style={{
            padding: '13px 16px',
            background: '#7a0000',
            display: 'flex', alignItems: 'center', gap: 6, minHeight: 54,
            boxShadow: '0 2px 8px rgba(26,46,74,0.3)',
          }}>
            <div style={{ flex: 1, color: '#fff' }}>{drawerTitle}</div>
            <Button
              type="text" size="small"
              onClick={() => { setOpen(false); setView('convs'); setActiveConv(null); }}
              style={{ fontSize: 18, color: 'rgba(255,255,255,0.85)', padding: '0 6px', lineHeight: 1 }}
            >✕</Button>
          </div>

          {/* ── CONVERSATIONS VIEW ── */}
          {view === 'convs' && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {conversations.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#bfbfbf' }}>
                  <MessageOutlined style={{ fontSize: 36, marginBottom: 8, display: 'block' }} />
                  No conversations yet.<br />
                  <span style={{ fontSize: 12 }}>Tap + to start a chat</span>
                </div>
              ) : conversations.map(conv => (
                <div
                  key={conv.id}
                  onClick={() => openConv(conv)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '11px 14px', cursor: 'pointer',
                    borderBottom: '1px solid #f5f5f5',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%',
                      background: conv.type === 'group' ? '#52c41a' : '#7a0000',
                      color: '#fff', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 13, fontWeight: 700,
                    }}>
                      {conv.type === 'group' ? <TeamOutlined /> : initials(conv.other_name || '?')}
                    </div>
                    {conv.type === 'direct' && conv.other_id && onlineUsers.has(conv.other_id) && (
                      <span style={{
                        position: 'absolute', bottom: 1, right: 1,
                        width: 10, height: 10, borderRadius: '50%',
                        background: '#52c41a', border: '2px solid #fff',
                      }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {convName(conv)}
                    </div>
                    {conv.type === 'direct' && conv.other_id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                        <span style={{
                          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                          background: onlineUsers.has(conv.other_id) ? '#52c41a' : '#d9d9d9',
                        }} />
                        <span style={{ fontSize: 12, color: onlineUsers.has(conv.other_id) ? '#52c41a' : '#bfbfbf', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {onlineUsers.has(conv.other_id) ? 'Online' : 'Offline'}
                        </span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#8c8c8c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
                        {conv.last_message || 'No messages yet'}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: '#bfbfbf' }}>{formatTime(conv.last_message_at)}</span>
                    {conv.unread_count > 0 && (
                      <span style={{
                        background: '#ff4d4f', color: '#fff', borderRadius: 10,
                        fontSize: 11, fontWeight: 700, padding: '1px 6px', minWidth: 18, textAlign: 'center',
                      }}>{conv.unread_count}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── CONTACTS VIEW ── */}
          {view === 'contacts' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #f0f0f0' }}>
                <Input
                  prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                  placeholder="Search staff members…"
                  value={contactSearch}
                  onChange={e => setContactSearch(e.target.value)}
                  allowClear
                  autoFocus
                />
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {filteredContacts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#bfbfbf', fontSize: 13 }}>No staff found</div>
                ) : filteredContacts.map(c => (
                  <div
                    key={c.id}
                    onClick={() => startDirect(c.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', cursor: 'pointer',
                      borderBottom: '1px solid #f5f5f5',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f4f8'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: '#7a0000', color: '#fff', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                      }}>
                        {initials(c.name)}
                      </div>
                      {onlineUsers.has(c.id) && (
                        <span style={{
                          position: 'absolute', bottom: 1, right: 1,
                          width: 10, height: 10, borderRadius: '50%',
                          background: '#52c41a', border: '2px solid #fff',
                        }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, color: '#1a1a2e' }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: onlineUsers.has(c.id) ? '#52c41a' : '#8c8c8c' }}>
                        {onlineUsers.has(c.id) ? 'Online' : (c.email || 'Offline')}
                      </div>
                    </div>
                    <MessageOutlined style={{ color: '#7a0000', fontSize: 16 }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── MESSAGES VIEW ── */}
          {view === 'messages' && (
            <>
              <div style={{
                flex: 1, overflowY: 'auto', padding: '14px 14px 8px',
                display: 'flex', flexDirection: 'column',
              }}>
                {loadingMsgs ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                ) : messages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#bfbfbf', marginTop: 40, fontSize: 13 }}>
                    No messages yet. Say hello!
                  </div>
                ) : messages.map(msg => {
                  const mine = msg.sender_id === myId;
                  return (
                    <div key={msg.id} style={{
                      display: 'flex',
                      flexDirection: mine ? 'row-reverse' : 'row',
                      alignItems: 'flex-end',
                      gap: 6,
                      marginBottom: 10,
                    }}>
                      {/* Avatar (others only) */}
                      {!mine && (
                        <div style={{
                          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                          background: '#1890ff', color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700,
                        }}>
                          {initials(msg.sender_name)}
                        </div>
                      )}

                      {/* Bubble column */}
                      <div style={{
                        display: 'flex', flexDirection: 'column',
                        alignItems: mine ? 'flex-end' : 'flex-start',
                        maxWidth: 'calc(100% - 42px)',
                      }}>
                        {!mine && activeConv && activeConv.type === 'group' && (
                          <div style={{ fontSize: 10.5, color: '#8c8c8c', marginBottom: 2, paddingLeft: 2 }}>
                            {msg.sender_name}
                          </div>
                        )}
                        {/* The bubble — display:inline-block lets it size to content */}
                        <div style={{
                          display: 'inline-block',
                          padding: '8px 12px',
                          borderRadius: mine ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          background: mine ? '#7a0000' : '#f0f0f0',
                          color: mine ? '#fff' : '#1a1a2e',
                          fontSize: 13.5,
                          lineHeight: 1.45,
                          overflowWrap: 'break-word',
                          whiteSpace: 'pre-wrap',
                          maxWidth: '100%',
                          wordBreak: 'normal',
                        }}>
                          {msg.message}
                        </div>
                        <div style={{ fontSize: 10.5, color: '#bfbfbf', marginTop: 3, paddingLeft: 2, paddingRight: 2 }}>
                          {formatTime(msg.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div style={{
                padding: '10px 12px 12px', borderTop: '1px solid #f0f0f0',
                display: 'flex', gap: 8, alignItems: 'flex-end',
                background: '#fafafa',
              }}>
                <textarea
                  ref={inputRef}
                  rows={1}
                  placeholder="Type a message… (Enter to send)"
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending}
                  style={{
                    flex: 1, border: '1.5px solid #e8e8e8', borderRadius: 22,
                    padding: '9px 16px', fontSize: 13.5, resize: 'none',
                    outline: 'none', fontFamily: 'inherit', background: '#fff',
                    maxHeight: 100, overflowY: 'auto', lineHeight: 1.4,
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#7a0000'; e.target.style.boxShadow = '0 0 0 2px rgba(26,46,74,0.15)'; }}
                  onBlur={e => { e.target.style.borderColor = '#e8e8e8'; e.target.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSend}
                  loading={sending}
                  disabled={!inputText.trim()}
                  style={{
                    borderRadius: '50%', width: 40, height: 40, flexShrink: 0, padding: 0,
                    background: inputText.trim() ? '#7a0000' : undefined,
                    border: 'none', boxShadow: inputText.trim() ? '0 2px 8px rgba(26,46,74,0.4)' : 'none',
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
